import * as React from "react";
import { IACLEmail } from "intake_shared/src/types";
import {
    assignmentWeeklyHours,
    WeeklyHours,
    isActiveCareCoordinator,
    canAutoAssignToCoordinator,
} from "intake_shared/src/assignment";
import store from "../../../store";
import { getCareCoordinatorACL } from "../../../store/acl";
import { TimeOffRange } from "../../../store/assignment";
import { PageHeader } from "../bits/PageHeader/PageHeader";
import { Button } from "../bits/Button/Button";
import { Card } from "../bits/Card/Card";
import { Dialog } from "../bits/Dialog/Dialog";
import { Select } from "../bits/Select/Select";
import Alert from "../bits/Alert/Alert";
import AssignmentBalance from "./AssignmentBalance";
import TimeOffCard from "./TimeOffCard";
import styles from "./Assignment.module.scss";

const weeklyHoursOptions = ([35, 40, 45] as WeeklyHours[]).map((value) => ({
    label: `${value} hours/week`,
    value,
}));

export default function CareCoordinators() {
    const [profileState, setProfileState] = React.useState<{
        acls: IACLEmail[];
        revision: number;
    }>({ acls: [], revision: 0 });
    const acls = profileState.acls;
    const [loading, setLoading] = React.useState(true);
    const [hasLoaded, setHasLoaded] = React.useState(false);
    const [error, setError] = React.useState(false);
    const [selected, setSelected] = React.useState<IACLEmail | null>(null);
    const [weeklyHours, setWeeklyHours] = React.useState<WeeklyHours>(40);
    const [savingHours, setSavingHours] = React.useState(false);
    const [hoursError, setHoursError] = React.useState("");
    const [hoursSaved, setHoursSaved] = React.useState(false);
    const [timeOffUpdate, setTimeOffUpdate] = React.useState<{
        email: string;
        ranges: TimeOffRange[];
    }>();
    const [childOverlayOpen, setChildOverlayOpen] = React.useState(false);
    const [showDiscard, setShowDiscard] = React.useState(false);
    const discardPrompt = React.useRef<HTMLDivElement>(null);
    const roster = React.useRef<HTMLDivElement>(null);
    const openingEmail = React.useRef("");
    const loadPending = React.useRef(false);
    const load = React.useCallback(async () => {
        if (loadPending.current) return;
        loadPending.current = true;
        setLoading(true);
        setError(false);
        try {
            const profiles = await getCareCoordinatorACL();
            setProfileState((previous) => ({
                acls: profiles,
                revision: previous.revision + 1,
            }));
            setHasLoaded(true);
        } catch {
            setError(true);
        } finally {
            loadPending.current = false;
            setLoading(false);
        }
    }, []);
    React.useEffect(() => {
        void load();
    }, [load]);
    const eligible = new Set(
        acls
            .filter(canAutoAssignToCoordinator)
            .map((acl) =>
                typeof acl.email === "string" ? acl.email.trim().toLowerCase() : ""
            )
            .filter(Boolean)
    );

    const manage = (coordinator: IACLEmail) => {
        if (savingHours || !isActiveCareCoordinator(coordinator)) return;
        openingEmail.current = coordinator.email.trim().toLowerCase();
        setSelected(coordinator);
        setWeeklyHours(assignmentWeeklyHours(coordinator.assignment?.weekly_hours));
        setHoursSaved(false);
        setHoursError("");
        setShowDiscard(false);
    };
    const hoursChanged = Boolean(
        selected &&
            weeklyHours !== assignmentWeeklyHours(selected.assignment?.weekly_hours)
    );
    const closeDetails = (discard = false) => {
        if (savingHours || childOverlayOpen) return;
        if (hoursChanged && !discard) {
            setShowDiscard(true);
            requestAnimationFrame(() => discardPrompt.current?.focus());
            return;
        }
        setShowDiscard(false);
        setSelected(null);
        requestAnimationFrame(() => {
            const currentButton = Array.from(
                roster.current?.querySelectorAll<HTMLButtonElement>(
                    "button[data-coordinator-email]"
                ) || []
            ).find(
                (button) => button.dataset.coordinatorEmail === openingEmail.current
            );
            const fallback =
                roster.current?.querySelector<HTMLElement>("#coordinator-roster");
            (currentButton || fallback)?.focus({ preventScroll: true });
        });
    };
    const saveHours = async (event: React.FormEvent) => {
        event.preventDefault();
        if (
            !selected ||
            !isActiveCareCoordinator(selected) ||
            savingHours ||
            !hoursChanged
        )
            return;
        setSavingHours(true);
        setHoursError("");
        setHoursSaved(false);
        const coordinator = selected;
        try {
            await store.saveAcl({
                email: coordinator.email,
                assignment: { weekly_hours: weeklyHours },
            });
            const updated = {
                ...coordinator,
                assignment: { ...coordinator.assignment, weekly_hours: weeklyHours },
            };
            setProfileState((previous) => ({
                ...previous,
                acls: previous.acls.map((acl) =>
                    acl.email === coordinator.email ? updated : acl
                ),
            }));
            setSelected(updated);
            setHoursSaved(true);
            setShowDiscard(false);
        } catch {
            setHoursError("Could not save weekly hours. Try again.");
        } finally {
            setSavingHours(false);
        }
    };

    return (
        <div className={`v2-page ${styles.page}`}>
            <PageHeader
                title="Care coordinators"
                meta={hasLoaded ? `${eligible.size} auto-assignable` : undefined}
            />
            {selected && (
                <Dialog
                    open
                    variant="drawer"
                    title="Manage coordinator"
                    ariaLabel={`Manage coordinator ${selected.email}`}
                    wrapperClassName={styles.managementDrawer}
                    onClose={() => closeDetails()}
                    focusManagement={{ paused: childOverlayOpen }}
                >
                    <div className={styles.managementContent}>
                        <h2 className={styles.coordinatorHeading}>
                            {selected.email}
                        </h2>
                        {showDiscard && (
                            <div
                                className={styles.discardPrompt}
                                ref={discardPrompt}
                                tabIndex={-1}
                                role="group"
                                aria-label="Unsaved weekly hours"
                            >
                                <p>Your weekly hours change has not been saved.</p>
                                <div className={styles.assignmentToolbar}>
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setShowDiscard(false);
                                            document
                                                .getElementById(
                                                    "coordinator-weekly-hours"
                                                )
                                                ?.focus();
                                        }}
                                    >
                                        Keep editing
                                    </Button>
                                    <Button
                                        variant="danger"
                                        onClick={() => closeDetails(true)}
                                    >
                                        Discard changes
                                    </Button>
                                </div>
                            </div>
                        )}
                        <Card classNames={styles.settingsCard}>
                            {hoursError && <Alert type="danger">{hoursError}</Alert>}
                            {hoursSaved && (
                                <Alert type="success">Weekly hours saved.</Alert>
                            )}
                            <form
                                className={styles.settingsRow}
                                onSubmit={saveHours}
                            >
                                <div className={styles.hoursField}>
                                    <label
                                        htmlFor="coordinator-weekly-hours"
                                        className={styles.toolbarLabel}
                                    >
                                        Weekly assignment hours
                                    </label>
                                    <Select
                                        inputId="coordinator-weekly-hours"
                                        aria-label="Weekly assignment hours"
                                        wrapperClass={styles.modeSelect}
                                        value={weeklyHoursOptions.find(
                                            (option) => option.value === weeklyHours
                                        )}
                                        options={weeklyHoursOptions}
                                        isClearable={false}
                                        isDisabled={savingHours || loading}
                                        onChange={(option) => {
                                            const next = option as {
                                                value: WeeklyHours;
                                            } | null;
                                            if (next) {
                                                setWeeklyHours(next.value);
                                                setHoursSaved(false);
                                                setShowDiscard(false);
                                            }
                                        }}
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={
                                        savingHours || loading || !hoursChanged
                                    }
                                >
                                    {savingHours ? "Saving…" : "Save weekly hours"}
                                </Button>
                                {hoursChanged && (
                                    <span className={styles.helper}>Not saved</span>
                                )}
                            </form>
                        </Card>
                        <TimeOffCard
                            key={selected.email}
                            email={selected.email}
                            onChanged={(ranges) =>
                                setTimeOffUpdate({ email: selected.email, ranges })
                            }
                            onOverlayChange={setChildOverlayOpen}
                        />
                    </div>
                </Dialog>
            )}
            {error && (
                <Alert type="danger" contentClassName={styles.alertRow}>
                    <span>
                        {hasLoaded
                            ? "Could not refresh care coordinators. Previous results are shown."
                            : "Could not load care coordinators."}
                    </span>
                    <Button variant="secondary" onClick={load}>
                        Try again
                    </Button>
                </Alert>
            )}
            {hasLoaded ? (
                <div ref={roster}>
                    <AssignmentBalance
                        acls={acls}
                        refreshKey={profileState.revision}
                        timeOffUpdate={timeOffUpdate}
                        onRefresh={load}
                        refreshing={loading}
                        rosterUnavailable={error}
                        onManageCoordinator={manage}
                        managementDisabled={savingHours}
                    />
                </div>
            ) : loading ? (
                <Card>
                    <p className={styles.helper} role="status">
                        Loading care coordinators…
                    </p>
                </Card>
            ) : null}
        </div>
    );
}
